import React, { useState, useEffect, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../SafeIcon';
import supabase from '../../../supabase/supabase';

const {
  FiChevronDown, FiChevronUp, FiCheck, FiEye, FiEyeOff,
  FiInfo, FiSave, FiAlertCircle, FiCheckCircle, FiPlus,
  FiEdit2, FiTrash2, FiX, FiMail, FiZap, FiExternalLink
} = FiIcons;

const ACCENT = '#c8f542';
const TABS = ['Store Details', 'General', 'Shipping', 'Payments', 'EmailJS Integration'];
const COUNTRIES = ['South Africa', 'United States', 'United Kingdom', 'Canada', 'Australia'];
const PAYMENT_PROVIDERS = [
  {
    id: 'payfast', name: 'PayFast',
    description: "South Africa's leading payment gateway. Accept cards, EFT, and more.",
    color: '#0077c8', hasSandbox: true,
    fields: [
      { key: 'merchant_id', label: 'Merchant ID', placeholder: 'e.g. 10000100', type: 'text' },
      { key: 'merchant_key', label: 'Merchant Key', placeholder: 'e.g. 46f0cd694581a', type: 'text' },
      { key: 'passphrase', label: 'Passphrase', placeholder: 'Your PayFast passphrase', type: 'password' },
    ],
  },
];

const useTokens = (isDark) => ({
  pageBg:  isDark ? 'bg-[#0f172a]'     : 'bg-[#fafbfc]',
  cardBg:  isDark ? 'bg-[#1e293b]'     : 'bg-white',
  border:  isDark ? 'border-[#334155]' : 'border-gray-200',
  title:   isDark ? 'text-white'        : 'text-gray-800',
  label:   isDark ? 'text-gray-300'     : 'text-gray-700',
  sub:     isDark ? 'text-gray-400'     : 'text-gray-500',
  muted:   isDark ? 'text-gray-500'     : 'text-gray-400',
  divider: isDark ? 'border-[#334155]' : 'border-gray-100',
  input:   isDark
    ? 'bg-[#0f172a] border-[#334155] text-white placeholder-gray-500 focus:border-[#c8f542] focus:ring-[#c8f542]/20'
    : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-[#c8f542] focus:ring-[#c8f542]/20',
});

// ─── Core: Save a JSON object value with Bulletproof RPC & Select Fallback ────
async function safeUpsertSetting(projectId, key, value) {
  if (!projectId) throw new Error('No project ID provided');

  const TIMEOUT_MS = 8000;
  let timerId;

  const timeoutPromise = new Promise((_, reject) => {
    timerId = setTimeout(() => {
      reject(new Error(`Database operation timed out after ${TIMEOUT_MS / 1000}s. Avoided infinite load.`));
    }, TIMEOUT_MS);
  });

  const dbOperation = async () => {
    // 1. Try the new Stored Procedure (RPC) first. This bypasses RLS and upsert bugs.
    const rpcRes = await supabase.rpc('upsert_store_setting', {
      p_project_id: projectId,
      p_key: key,
      p_value: value
    });

    if (!rpcRes.error) return rpcRes;

    // 2. Fallback if the RPC hasn't migrated yet.
    console.warn('RPC failed, falling back to manual delete/insert:', rpcRes.error.message);
    
    await supabase.from('store_settings_1773100000000')
      .delete()
      .eq('project_id', projectId)
      .eq('key', key)
      .select(); // Prevent hang
      
    const insRes = await supabase.from('store_settings_1773100000000')
      .insert({ project_id: projectId, key, value, updated_at: new Date().toISOString() })
      .select(); // Prevent hang
      
    if (insRes.error) throw new Error(insRes.error.message);
    return insRes;
  };

  try {
    const result = await Promise.race([dbOperation(), timeoutPromise]);
    clearTimeout(timerId);
    return result;
  } catch (err) {
    clearTimeout(timerId);
    console.error(`[safeUpsertSetting] failed for key "${key}":`, err);
    throw err;
  }
}

// ─── Shared UI Components ─────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose, isDark }) => {
  useEffect(() => {
    const t = setTimeout(onClose, type === 'error' ? 6000 : 3500);
    return () => clearTimeout(t);
  }, [onClose, type]);

  const styles = {
    success: isDark ? 'bg-emerald-900/80 border-emerald-700/60 text-emerald-200' : 'bg-emerald-50 border-emerald-300 text-emerald-800',
    error:   isDark ? 'bg-red-900/80 border-red-700/60 text-red-200' : 'bg-red-50 border-red-300 text-red-800',
  };
  const Icon = type === 'success' ? FiCheckCircle : FiAlertCircle;
  return (
    <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center space-x-3 px-5 py-3 rounded-2xl border shadow-2xl max-w-md ${styles[type]}`}>
      <SafeIcon icon={Icon} className="w-5 h-5 shrink-0" />
      <span className="text-sm font-bold">{msg}</span>
      <button type="button" onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <SafeIcon icon={FiX} className="w-4 h-4" />
      </button>
    </div>
  );
};

const ErrorBanner = ({ error, isDark }) => {
  if (!error) return null;
  return (
    <div className={`flex items-start space-x-3 p-4 rounded-xl border ${
      isDark ? 'bg-red-900/20 border-red-700/40 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
    }`}>
      <SafeIcon icon={FiAlertCircle} className="w-5 h-5 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-bold">Error</p>
        <p className="text-xs mt-1 opacity-80 break-words">{error}</p>
      </div>
    </div>
  );
};

const SaveBtn = ({ saving, onClick, label = 'Save Changes' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={saving}
    className="text-black px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm disabled:opacity-70 flex items-center space-x-2 hover:brightness-110"
    style={{ backgroundColor: ACCENT }}
  >
    {saving
      ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
      : <SafeIcon icon={FiSave} className="w-4 h-4" />}
    <span>{saving ? 'Saving...' : label}</span>
  </button>
);

const Input = ({ tokens, value, onChange, placeholder, type = 'text', prefix, readOnly }) => (
  <div className="relative">
    {prefix && <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium ${tokens.muted}`}>{prefix}</span>}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`w-full p-3 border rounded-xl outline-none text-sm transition-all focus:ring-2 ${tokens.input} ${prefix ? 'pl-8' : ''} ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
    />
  </div>
);

const Select = ({ tokens, value, onChange, children }) => (
  <select value={value} onChange={onChange}
    className={`w-full p-3 border rounded-xl outline-none text-sm transition-all focus:ring-2 cursor-pointer ${tokens.input}`}>
    {children}
  </select>
);

const Toggle = ({ enabled, onChange }) => (
  <button type="button" onClick={() => onChange(!enabled)}
    className="relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0"
    style={{ backgroundColor: enabled ? ACCENT : '#334155' }}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full shadow transition-transform duration-200 ${enabled ? 'translate-x-5 bg-black' : 'translate-x-0 bg-white'}`} />
  </button>
);

const Checkbox = ({ checked, onChange }) => (
  <div onClick={() => onChange(!checked)}
    className="w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all shrink-0"
    style={checked ? { backgroundColor: ACCENT, borderColor: ACCENT } : { borderColor: '#475569' }}>
    {checked && <div className="w-2.5 h-2.5 bg-black rounded-full" />}
  </div>
);

const SecretField = ({ label, value, onChange, placeholder, tokens }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      {label && <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>{label}</label>}
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full p-3 pr-10 border rounded-xl outline-none text-sm transition-all focus:ring-2 ${tokens.input}`}
        />
        <button type="button" onClick={() => setShow(v => !v)}
          className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${tokens.muted} hover:text-[#c8f542]`}>
          <SafeIcon icon={show ? FiEyeOff : FiEye} className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const SandboxToggle = ({ enabled, onChange, isDark }) => (
  <div onClick={() => onChange(!enabled)}
    className={`flex items-start space-x-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
      enabled
        ? isDark ? 'bg-amber-900/20 border-amber-700/50' : 'bg-amber-50 border-amber-300'
        : isDark ? 'bg-[#0f172a] border-[#334155] hover:border-[#475569]' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
    }`}>
    <div className="w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 shrink-0 transition-all"
      style={enabled ? { backgroundColor: ACCENT, borderColor: ACCENT } : { borderColor: '#475569' }}>
      {enabled && <SafeIcon icon={FiCheck} className="w-3 h-3 text-black" />}
    </div>
    <div className="flex-1">
      <span className={`text-sm font-bold ${enabled ? (isDark ? 'text-amber-300' : 'text-amber-800') : (isDark ? 'text-gray-300' : 'text-gray-700')}`}>
        Sandbox / Testing Mode
      </span>
      {enabled && <span className={`ml-2 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-200 text-amber-800'}`}>Active</span>}
      <p className={`text-xs mt-0.5 ${enabled ? (isDark ? 'text-amber-400' : 'text-amber-700') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
        {enabled ? 'Sandbox mode — no real payments will be processed.' : 'Payments are processed in live mode.'}
      </p>
    </div>
    <Toggle enabled={enabled} onChange={() => {}} />
  </div>
);

const Loading = () => (
  <div className="flex justify-center py-16">
    <div className="w-8 h-8 border-4 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin" />
  </div>
);

const ComingSoonTab = ({ isDark }) => (
  <div className={`flex flex-col items-center justify-center py-20 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
    <p className="text-sm">This section is coming soon.</p>
  </div>
);

// ─── General Tab ──────────────────────────────────────────────────────────────
function GeneralTab({ projectId, isDark }) {
  const tokens = useTokens(isDark);
  const [form, setForm] = useState({
    currency: 'South African Rand (R)', currencySymbol: 'R',
    currencySymbolPosition: '$19.91', weightUnit: 'kg - kilogram',
    dimensionsUnit: 'm - meter', storeProcess: 'Add to Cart',
    nextInvoiceNumber: '762', abandonedInactiveFor: '1 Hour',
    notifyLowInventory: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await supabase.from('store_settings_1773100000000')
          .select('value').eq('project_id', projectId).eq('key', 'store_general').maybeSingle();
        if (data?.value) setForm(p => ({ ...p, ...data.value }));
      } catch (_) {}
      setLoading(false);
    })();
  }, [projectId]);

  const handleSave = async () => {
    if (!projectId) { setError('No project selected.'); return; }
    setSaving(true); setError(null);
    try {
      await safeUpsertSetting(projectId, 'store_general', form);
      setToast({ msg: 'General settings saved!', type: 'success' });
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  const selectFields = [
    { label: 'Currency', key: 'currency', options: ['South African Rand (R)', 'US Dollar ($)', 'Euro (€)', 'British Pound (£)'] },
    { label: 'Weight Unit', key: 'weightUnit', options: ['kg - kilogram', 'g - gram', 'lb - pound', 'oz - ounce'] },
    { label: 'Dimensions Unit', key: 'dimensionsUnit', options: ['m - meter', 'cm - centimeter', 'mm - millimeter', 'in - inch'] },
    { label: 'Store Process', key: 'storeProcess', options: ['Add to Cart', 'Buy Now'] },
    { label: 'Mark orders as abandoned if inactive for', key: 'abandonedInactiveFor', options: ['1 Hour', '2 Hours', '24 Hours'] },
  ];

  return (
    <div className="space-y-8">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} isDark={isDark} />}
      <div>
        <h3 className={`text-lg font-bold mb-1 ${tokens.title}`}>General</h3>
        <p className={`text-sm mb-4 ${tokens.sub}`}>General global settings for your e-commerce store.</p>
        <ErrorBanner error={error} isDark={isDark} />
        <div className="space-y-5 max-w-2xl mt-4">
          {selectFields.map(f => (
            <div key={f.key}>
              <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>{f.label}</label>
              <Select tokens={tokens} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
            </div>
          ))}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>Currency Symbol</label>
              <Input tokens={tokens} value={form.currencySymbol} onChange={e => setForm({ ...form, currencySymbol: e.target.value })} />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>Symbol Position</label>
              <Select tokens={tokens} value={form.currencySymbolPosition} onChange={e => setForm({ ...form, currencySymbolPosition: e.target.value })}>
                <option value="$19.91">$19.91</option>
                <option value="19.91$">19.91$</option>
              </Select>
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>Next Invoice Number</label>
            <Input tokens={tokens} type="number" value={form.nextInvoiceNumber} onChange={e => setForm({ ...form, nextInvoiceNumber: e.target.value })} />
          </div>
          <label className="flex items-start space-x-3 cursor-pointer">
            <Checkbox checked={form.notifyLowInventory} onChange={v => setForm({ ...form, notifyLowInventory: v })} />
            <div>
              <span className={`text-sm font-medium ${tokens.title}`}>Notify on low product inventory</span>
              <p className={`text-xs mt-0.5 ${tokens.sub}`}>Send a notification if a product quantity falls below a set threshold</p>
            </div>
          </label>
        </div>
      </div>
      <div className={`pt-6 border-t flex justify-end ${tokens.divider}`}>
        <SaveBtn saving={saving} onClick={handleSave} />
      </div>
    </div>
  );
}

// ─── Store Details Tab ────────────────────────────────────────────────────────
function StoreDetailsTab({ projectId, isDark }) {
  const tokens = useTokens(isDark);
  const [form, setForm] = useState({
    orderEmail: '', companyName: '', companyId: '', address: '',
    city: '', state: '', zip: '', country: 'South Africa', phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await supabase.from('store_settings_1773100000000')
          .select('value').eq('project_id', projectId).eq('key', 'store_details').maybeSingle();
        if (data?.value) setForm(p => ({ ...p, ...data.value }));
      } catch (_) {}
      setLoading(false);
    })();
  }, [projectId]);

  const handleSave = async () => {
    if (!projectId) { setError('No project selected.'); return; }
    setSaving(true); setError(null);
    try {
      await safeUpsertSetting(projectId, 'store_details', form);
      setToast({ msg: 'Store details saved!', type: 'success' });
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-8">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} isDark={isDark} />}
      <section>
        <h3 className={`text-lg font-bold mb-1 ${tokens.title}`}>Order Notification Email</h3>
        <p className={`text-sm mb-4 ${tokens.sub}`}>Email that receives notifications when a new order is made. Also used as reply-to in confirmation emails.</p>
        <Input tokens={tokens} value={form.orderEmail} onChange={e => setForm({ ...form, orderEmail: e.target.value })} placeholder="e.g. orders@mystore.com" type="email" />
      </section>
      <section>
        <h3 className={`text-lg font-bold mb-1 ${tokens.title}`}>Store Details</h3>
        <p className={`text-sm mb-4 ${tokens.sub}`}>These details will appear in order emails to your customers.</p>
        <ErrorBanner error={error} isDark={isDark} />
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 max-w-2xl mt-4">
          {[{ label: 'Company Name', key: 'companyName', placeholder: 'My Store' }, { label: 'Company ID', key: 'companyId', placeholder: 'Optional' }].map(f => (
            <div key={f.key}>
              <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>{f.label}</label>
              <Input tokens={tokens} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} />
            </div>
          ))}
          <div className="col-span-2">
            <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>Address</label>
            <Input tokens={tokens} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
          {[{ label: 'City', key: 'city' }, { label: 'State/Province', key: 'state' }, { label: 'Postal/ZIP Code', key: 'zip' }].map(f => (
            <div key={f.key}>
              <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>{f.label}</label>
              <Input tokens={tokens} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
            </div>
          ))}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>Country</label>
            <Select tokens={tokens} value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${tokens.label}`}>Phone</label>
            <Input tokens={tokens} type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
      </section>
      <div className={`pt-6 border-t flex justify-end ${tokens.divider}`}>
        <SaveBtn saving={saving} onClick={handleSave} />
      </div>
    </div>
  );
}

// ─── Shipping Modal ───────────────────────────────────────────────────────────
function ShippingModal({ isOpen, onClose, onSave, initialData, currencySymbol, initialType = 'Shipping', isDark }) {
  const tokens = useTokens(isDark);
  const [form, setForm] = useState({ type: initialType, dependsOn: 'Price Range', name: '', from: '0.00', to: '0.00', cost: '0.00', pickupLocations: [''] });

  useEffect(() => {
    if (initialData) setForm({ ...initialData, type: initialData.type || initialType, pickupLocations: initialData.pickupLocations || [''] });
    else if (isOpen) setForm({ type: initialType, dependsOn: 'Price Range', name: '', from: '0.00', to: '0.00', cost: '0.00', pickupLocations: [''] });
  }, [initialData, isOpen, initialType]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`rounded-2xl shadow-2xl w-full max-w-[500px] max-h-[90vh] overflow-y-auto m-4 border ${isDark ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100'}`}>
        <div className="p-8 space-y-6">
          <h2 className={`text-center text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Shipping Option</h2>
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Costs Depend On</label>
            <Select tokens={tokens} value={form.dependsOn} onChange={e => setForm({ ...form, dependsOn: e.target.value })}>
              {['Price Range', 'Weight Range', 'Flat Rate'].map(o => <option key={o}>{o}</option>)}
            </Select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Name</label>
            <Input tokens={tokens} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Standard Shipping" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[{ label: 'From', key: 'from' }, { label: 'To', key: 'to' }].map(f => (
              <div key={f.key}>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{f.label}</label>
                <Input tokens={tokens} type="number" prefix={currencySymbol} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
              </div>
            ))}
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Shipping Cost</label>
            <Input tokens={tokens} type="number" prefix={currencySymbol} value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} />
          </div>
          {form.type === 'Pickup' && (
            <div className="space-y-3">
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Pickup Locations</label>
              {(form.pickupLocations || []).map((loc, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <Input tokens={tokens} value={loc} onChange={e => { const l = [...form.pickupLocations]; l[idx] = e.target.value; setForm({ ...form, pickupLocations: l }); }} placeholder="Enter location" />
                  <button type="button" onClick={() => { const l = form.pickupLocations.filter((_, i) => i !== idx); setForm({ ...form, pickupLocations: l.length ? l : [''] }); }}
                    className="text-black px-4 py-2.5 rounded-xl text-sm font-bold shrink-0" style={{ backgroundColor: ACCENT }}>Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => setForm({ ...form, pickupLocations: [...(form.pickupLocations || []), ''] })}
                className="text-black px-4 py-2 rounded-xl text-sm font-bold flex items-center space-x-1.5" style={{ backgroundColor: ACCENT }}>
                <SafeIcon icon={FiPlus} className="w-4 h-4" /><span>Add Location</span>
              </button>
            </div>
          )}
          <div className="flex justify-end items-center space-x-4 pt-4">
            <button type="button" onClick={onClose} className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cancel</button>
            <button type="button" onClick={() => onSave(form)} className="text-black px-8 py-2.5 rounded-xl font-bold text-sm" style={{ backgroundColor: ACCENT }}>Confirm</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shipping Tab ─────────────────────────────────────────────────────────────
function ShippingTab({ projectId, isDark }) {
  const tokens = useTokens(isDark);
  const [settings, setSettings] = useState({ rules: [], freeShippingBar: false });
  const [currencySymbol, setCurrencySymbol] = useState('R');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, data: null, editIndex: null, type: 'Shipping' });

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        const [s, g] = await Promise.all([
          supabase.from('store_settings_1773100000000').select('value').eq('project_id', projectId).eq('key', 'store_shipping').maybeSingle(),
          supabase.from('store_settings_1773100000000').select('value').eq('project_id', projectId).eq('key', 'store_general').maybeSingle(),
        ]);
        if (s.data?.value) setSettings(p => ({ ...p, ...s.data.value }));
        if (g.data?.value?.currencySymbol) setCurrencySymbol(g.data.value.currencySymbol);
      } catch (_) {}
      setLoading(false);
    })();
  }, [projectId]);

  const saveToDB = async (next) => {
    try {
      await safeUpsertSetting(projectId, 'store_shipping', next);
      setToast({ msg: 'Shipping settings saved!', type: 'success' });
      setSettings(next);
    } catch (e) {
      setToast({ msg: e.message || 'Failed to save.', type: 'error' });
    }
  };

  const handleSaveRule = (rule) => {
    const rules = [...settings.rules];
    if (modal.editIndex !== null) rules[modal.editIndex] = rule; else rules.push(rule);
    setModal({ isOpen: false, data: null, editIndex: null, type: 'Shipping' });
    saveToDB({ ...settings, rules });
  };

  const handleDelete = (idx) => {
    if (!window.confirm('Delete this shipping option?')) return;
    saveToDB({ ...settings, rules: settings.rules.filter((_, i) => i !== idx) });
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} isDark={isDark} />}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`lg:col-span-2 p-6 rounded-xl border ${tokens.cardBg} ${tokens.border}`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-lg font-bold ${tokens.title}`}>Shipping Options</h3>
            <div className="flex items-center space-x-3">
              {[{ label: 'Add Option', type: 'Shipping' }, { label: 'Pickup', type: 'Pickup' }].map(b => (
                <button type="button" key={b.type} onClick={() => setModal({ isOpen: true, data: null, editIndex: null, type: b.type })}
                  className="text-black px-4 py-2 rounded-xl text-sm font-bold flex items-center space-x-1.5" style={{ backgroundColor: ACCENT }}>
                  <SafeIcon icon={FiPlus} className="w-4 h-4" /><span>{b.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {settings.rules.map((rule, idx) => (
              <div key={idx} className={`flex items-center justify-between p-4 rounded-xl border ${isDark ? 'bg-[#0f172a] border-[#334155]' : 'bg-white border-gray-200'}`}>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className={`font-bold text-sm ${tokens.title}`}>{rule.name || 'Unnamed'}</span>
                    <span className="px-2 py-0.5 text-[10px] font-black uppercase rounded-full text-black" style={{ backgroundColor: ACCENT }}>{rule.type}</span>
                  </div>
                  <p className={`text-xs mt-1 ${tokens.sub}`}>{rule.dependsOn}: {currencySymbol}{rule.from} – {currencySymbol}{rule.to}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`font-bold text-sm px-3 py-1 rounded-lg border ${isDark ? 'bg-[#1e293b] border-[#334155] text-white' : 'bg-gray-50 border-gray-100 text-gray-800'}`}>{currencySymbol}{rule.cost}</span>
                  <button type="button" onClick={() => setModal({ isOpen: true, data: rule, editIndex: idx, type: rule.type || 'Shipping' })}
                    className={`p-2 rounded-lg ${isDark ? 'text-gray-500 hover:text-[#c8f542]' : 'text-gray-400 hover:text-[#c8f542]'}`}>
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => handleDelete(idx)}
                    className={`p-2 rounded-lg ${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}>
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {settings.rules.length === 0 && (
              <div className={`text-center py-10 rounded-xl border border-dashed ${isDark ? 'border-[#334155] text-gray-600' : 'border-gray-200 text-gray-400'}`}>
                <p className="text-sm">No shipping options yet.</p>
              </div>
            )}
          </div>
        </div>
        <div className={`p-6 rounded-xl border ${tokens.cardBg} ${tokens.border}`}>
          <h3 className={`text-lg font-bold mb-4 ${tokens.title}`}>Options</h3>
          <div className="flex items-start space-x-4">
            <Toggle enabled={settings.freeShippingBar} onChange={v => saveToDB({ ...settings, freeShippingBar: v })} />
            <div>
              <span className={`text-sm font-bold block ${tokens.title}`}>Free Shipping Bar</span>
              <span className={`text-xs block mt-1 ${tokens.sub}`}>Show a bar encouraging customers to unlock free shipping.</span>
            </div>
          </div>
        </div>
      </div>
      <ShippingModal isOpen={modal.isOpen} onClose={() => setModal({ isOpen: false, data: null, editIndex: null, type: 'Shipping' })}
        onSave={handleSaveRule} initialData={modal.data} currencySymbol={currencySymbol} initialType={modal.type} isDark={isDark} />
    </div>
  );
}

// ─── Provider Card ────────────────────────────────────────────────────────────
function ProviderCard({ provider, isActive, isExpanded, onToggle, onExpand, projectId, isDark }) {
  const tokens = useTokens(isDark);
  const [credentials, setCredentials] = useState({});
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [isEnabled, setIsEnabled] = useState(isActive);
  const [sandboxMode, setSandboxMode] = useState(true);

  useEffect(() => {
    if ((isActive || isExpanded) && projectId) loadCredentials();
  }, [isExpanded, projectId]);

  const loadCredentials = async () => {
    setLoadingCreds(true);
    try {
      const { data } = await supabase.from('store_settings_1773100000000').select('value')
        .eq('project_id', projectId).eq('key', `payment_${provider.id}`).maybeSingle();
      if (data?.value) {
        setCredentials(data.value.credentials || {});
        setIsEnabled(data.value.enabled ?? false);
        if (provider.hasSandbox) setSandboxMode(data.value.sandbox_mode !== false);
      }
    } catch (_) {}
    setLoadingCreds(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { enabled: isEnabled, credentials, ...(provider.hasSandbox ? { sandbox_mode: sandboxMode } : {}) };
      await safeUpsertSetting(projectId, `payment_${provider.id}`, payload);
      setToast({ msg: `${provider.name} settings saved!`, type: 'success' });
    } catch (e) {
      setToast({ msg: e.message || 'Failed to save.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    const next = !isEnabled;
    setIsEnabled(next);
    onToggle(provider.id, next);
    if (next) onExpand(provider.id);
  };

  return (
    <div className={`rounded-xl border-2 transition-all duration-200 overflow-hidden ${isEnabled ? (isDark ? 'border-[#c8f542]/40' : 'border-[#c8f542]/60') : (isDark ? 'border-[#334155]' : 'border-gray-200')}`}>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} isDark={isDark} />}
      <div onClick={() => isEnabled && onExpand(provider.id)}
        className={`flex items-center justify-between px-5 py-4 cursor-pointer ${isEnabled ? (isDark ? 'bg-[#1e293b]' : 'bg-[#c8f542]/5') : (isDark ? 'bg-[#1e293b]' : 'bg-white')}`}>
        <div className="flex items-center space-x-4">
          <button type="button" onClick={e => { e.stopPropagation(); handleToggle(); }}>
            <Toggle enabled={isEnabled} onChange={() => {}} />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <span className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{provider.name}</span>
              {isEnabled && <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full text-black" style={{ backgroundColor: ACCENT }}>Active</span>}
              {isEnabled && provider.hasSandbox && sandboxMode && <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>Sandbox</span>}
            </div>
            <p className={`text-xs mt-0.5 max-w-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{provider.description}</p>
          </div>
        </div>
        {isEnabled && (
          <button type="button" onClick={e => { e.stopPropagation(); onExpand(provider.id); }} className={isDark ? 'text-gray-500' : 'text-gray-400'}>
            <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-5 h-5" />
          </button>
        )}
      </div>
      {isEnabled && isExpanded && (
        <div className={`px-5 pb-5 pt-4 border-t ${isDark ? 'bg-[#0f172a] border-[#334155]' : 'bg-gray-50/50 border-gray-100'}`}>
          <div className={`flex items-center space-x-2 text-xs rounded-xl px-3 py-2 mb-5 border ${isDark ? 'bg-amber-900/20 border-amber-700/40 text-amber-400' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
            <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 shrink-0" />
            <span>Enter your {provider.name} credentials below.</span>
          </div>
          {loadingCreds ? <Loading /> : (
            <>
              {provider.hasSandbox && <div className="mb-5"><SandboxToggle enabled={sandboxMode} onChange={setSandboxMode} isDark={isDark} /></div>}
              <div className={`rounded-xl border overflow-hidden mb-5 ${isDark ? 'border-[#334155]' : 'border-gray-200'}`}>
                <table className="w-full">
                  <thead className={isDark ? 'bg-[#1e293b] border-b border-[#334155]' : 'bg-gray-100 border-b border-gray-200'}>
                    <tr>
                      {['Field', 'Value'].map(h => (
                        <th key={h} className={`text-left text-[10px] font-black uppercase tracking-wider px-4 py-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-[#334155]' : 'divide-gray-100'}`}>
                    {provider.fields.map(field => (
                      <tr key={field.key} className={isDark ? 'bg-[#0f172a]' : 'bg-white'}>
                        <td className="px-4 py-3"><span className={`text-sm font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{field.label}</span></td>
                        <td className="px-4 py-3">
                          {field.type === 'password'
                            ? <SecretField tokens={tokens} value={credentials[field.key] || ''} onChange={e => setCredentials(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} />
                            : <Input tokens={tokens} value={credentials[field.key] || ''} onChange={e => setCredentials(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <SaveBtn saving={saving} onClick={handleSave} label={`Save ${provider.name} Settings`} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Payments Tab ─────────────────────────────────────────────────────────────
function PaymentsTab({ projectId, isDark }) {
  const tokens = useTokens(isDark);
  const [activeProviders, setActiveProviders] = useState({});
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await supabase.from('store_settings_1773100000000')
          .select('key,value').eq('project_id', projectId).like('key', 'payment_%');
        if (data) {
          const map = {};
          data.forEach(row => { map[row.key.replace('payment_', '')] = row.value?.enabled ?? false; });
          setActiveProviders(map);
          const first = Object.entries(map).find(([, v]) => v)?.[0];
          if (first) setExpandedProvider(first);
        }
      } catch (_) {}
      setLoading(false);
    })();
  }, [projectId]);

  const handleToggle = (id, next) => {
    setActiveProviders(p => ({ ...p, [id]: next }));
    if (!next && expandedProvider === id) setExpandedProvider(null);
  };

  if (loading) return <Loading />;
  const activeCount = Object.values(activeProviders).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`text-lg font-bold mb-1 ${tokens.title}`}>Payment Providers</h3>
        <p className={`text-sm ${tokens.sub}`}>
          Enable and configure your payment providers.
          {activeCount > 0 && <span className="ml-2 font-bold" style={{ color: ACCENT }}>{activeCount} active</span>}
        </p>
      </div>
      <div className="space-y-3">
        {PAYMENT_PROVIDERS.map(p => (
          <ProviderCard key={p.id} provider={p} isActive={!!activeProviders[p.id]}
            isExpanded={expandedProvider === p.id} onToggle={handleToggle}
            onExpand={id => setExpandedProvider(prev => prev === id ? null : id)}
            projectId={projectId} isDark={isDark} />
        ))}
      </div>
    </div>
  );
}

// ─── EmailJS Integration Tab (Dedicated DB Table) ────────────────────────────
function EmailJSIntegrationTab({ projectId, isDark }) {
  const tokens = useTokens(isDark);

  const [form, setForm] = useState({
    serviceId: '',
    templateId: '',
    publicKey: ''
  });

  const [loaded, setLoaded]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus]   = useState(null);

  useEffect(() => {
    if (!projectId) { setLoaded(true); return; }
    let alive = true;

    (async () => {
      setLoaded(false);
      setStatus(null);
      try {
        const { data, error } = await supabase
          .from('store_email_configs')
          .select('service_id, template_id, public_key')
          .eq('project_id', projectId)
          .maybeSingle();

        if (error) throw error;

        if (alive && data) {
          setForm({
            serviceId: data.service_id || '',
            templateId: data.template_id || '',
            publicKey: data.public_key || ''
          });
        }
      } catch (e) {
        console.error('[EmailJS Load Error]', e);
        if (alive) {
          setStatus({
            type: 'error',
            title: 'Failed to Load Settings',
            details: e.message || 'An unknown error occurred while loading.'
          });
        }
      } finally {
        if (alive) setLoaded(true);
      }
    })();

    return () => { alive = false; };
  }, [projectId]);

  const handleSave = async () => {
    if (!projectId) {
      setStatus({ type: 'error', title: 'Action Denied', details: 'No project selected. Open a store project first.' });
      return;
    }

    const sId = (form.serviceId || '').trim();
    const tId = (form.templateId || '').trim();
    const pKey = (form.publicKey || '').trim();

    if (!sId || !tId || !pKey) {
      setStatus({ type: 'error', title: 'Validation Error', details: 'All three fields (Service ID, Template ID, Public Key) are required.' });
      return;
    }

    setSaving(true);
    setStatus(null);

    const TIMEOUT_MS = 8000;
    let timerId;

    const timeoutPromise = new Promise((_, reject) => {
      timerId = setTimeout(() => {
        reject(new Error(`Database operation timed out after ${TIMEOUT_MS / 1000}s. Avoided infinite load.`));
      }, TIMEOUT_MS);
    });

    const dbOperation = async () => {
      // Manual delete and insert bypasses any potential postgres upsert locks.
      await supabase
        .from('store_email_configs')
        .delete()
        .eq('project_id', projectId)
        .select();

      const { error: insertError } = await supabase
        .from('store_email_configs')
        .insert({
          project_id: projectId,
          service_id: sId,
          template_id: tId,
          public_key: pKey,
          updated_at: new Date().toISOString()
        })
        .select();

      if (insertError) throw insertError;
      return true;
    };

    try {
      await Promise.race([dbOperation(), timeoutPromise]);
      clearTimeout(timerId);
      setStatus({ type: 'success', title: 'Settings Saved', details: 'Your EmailJS credentials have been saved successfully.' });
    } catch (e) {
      clearTimeout(timerId);
      console.error('[EmailJS Save Error]', e);
      setStatus({ type: 'error', title: 'Failed to Save', details: e.message || 'An unexpected error occurred while saving to the database.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    const sId = (form.serviceId || '').trim();
    const tId = (form.templateId || '').trim();
    const pKey = (form.publicKey || '').trim();

    if (!sId || !tId || !pKey) {
      setStatus({ type: 'error', title: 'Validation Error', details: 'Save your credentials first before sending a test email.' });
      return;
    }

    setTesting(true);
    setStatus(null);

    try {
      const res = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: sId,
          template_id: tId,
          user_id: pKey,
          template_params: {
            to_name: 'Store Owner',
            to_email: 'test@example.com',
            order_number: 'TEST-001',
            order_total: 'R0.00',
            order_items: '1x Test Product — R0.00',
            payment_method: 'Test',
            payment_status: 'successful',
            store_name: 'My Store',
          },
        }),
      });

      if (res.ok) {
        setStatus({ type: 'success', title: 'Test Email Sent', details: 'Check your inbox! The test email was dispatched successfully.' });
      } else {
        const txt = await res.text();
        throw new Error(txt || `HTTP Error: ${res.status}`);
      }
    } catch (e) {
      console.error('[EmailJS Test Error]', e);
      setStatus({ type: 'error', title: 'Test Failed', details: e.message || 'Could not send test email.' });
    } finally {
      setTesting(false);
    }
  };

  if (!loaded) return <Loading />;

  const allFilled = form.serviceId.trim() && form.templateId.trim() && form.publicKey.trim();

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: ACCENT }}>
            <SafeIcon icon={FiZap} className="w-5 h-5 text-black" />
          </div>
          <h3 className={`text-lg font-bold ${tokens.title}`}>EmailJS Integration</h3>
        </div>
        <p className={`text-sm ${tokens.sub}`}>Connect EmailJS to automatically send order confirmation emails to your customers.</p>
        <a href="https://www.emailjs.com/" target="_blank" rel="noreferrer" className="inline-flex items-center space-x-1 mt-2 text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors">
          <span>Create a free account at emailjs.com</span>
          <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
        </a>
      </div>

      {status && (
        <div className={`flex items-start space-x-3 p-4 rounded-xl border ${status.type === 'success' ? (isDark ? 'bg-emerald-900/20 border-emerald-700/40 text-emerald-300' : 'bg-emerald-50 border-emerald-200 text-emerald-700') : (isDark ? 'bg-red-900/20 border-red-700/40 text-red-300' : 'bg-red-50 border-red-200 text-red-700')}`}>
          <SafeIcon icon={status.type === 'success' ? FiCheckCircle : FiAlertCircle} className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold">{status.title}</p>
            <p className="text-xs mt-1 opacity-90 break-words leading-relaxed">{status.details}</p>
          </div>
          <button type="button" onClick={() => setStatus(null)} className="opacity-50 hover:opacity-100 p-1"><SafeIcon icon={FiX} className="w-4 h-4" /></button>
        </div>
      )}

      {!projectId && (
        <div className={`flex items-start space-x-3 p-4 rounded-xl border ${isDark ? 'bg-amber-900/20 border-amber-700/40 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="text-sm font-medium">No store project selected. Please open a project to save settings.</p>
        </div>
      )}

      <div className={`rounded-2xl border overflow-hidden ${isDark ? 'border-[#334155]' : 'border-gray-200'}`}>
        <div className={`p-5 border-b ${isDark ? 'border-[#334155]' : 'border-gray-100'}`}>
          <label className={`block text-xs font-black uppercase tracking-wider mb-1 ${tokens.muted}`}>Service ID</label>
          <p className={`text-xs mb-3 ${tokens.sub}`}>Found in EmailJS → Email Services</p>
          <input type="text" value={form.serviceId} onChange={e => { setForm({ ...form, serviceId: e.target.value }); setStatus(null); }} placeholder="service_xxxxxxx" className={`w-full p-3 border rounded-xl outline-none text-sm transition-all focus:ring-2 font-mono ${tokens.input}`} />
        </div>
        <div className={`p-5 border-b ${isDark ? 'border-[#334155]' : 'border-gray-100'}`}>
          <label className={`block text-xs font-black uppercase tracking-wider mb-1 ${tokens.muted}`}>Template ID</label>
          <p className={`text-xs mb-3 ${tokens.sub}`}>Found in EmailJS → Email Templates</p>
          <input type="text" value={form.templateId} onChange={e => { setForm({ ...form, templateId: e.target.value }); setStatus(null); }} placeholder="template_xxxxxxx" className={`w-full p-3 border rounded-xl outline-none text-sm transition-all focus:ring-2 font-mono ${tokens.input}`} />
        </div>
        <div className="p-5">
          <label className={`block text-xs font-black uppercase tracking-wider mb-1 ${tokens.muted}`}>Public Key</label>
          <p className={`text-xs mb-3 ${tokens.sub}`}>Found in EmailJS → Account → General → Public Key</p>
          <input type="text" value={form.publicKey} onChange={e => { setForm({ ...form, publicKey: e.target.value }); setStatus(null); }} placeholder="xxxxxxxxxxxxxxxxxxxxxxx" className={`w-full p-3 border rounded-xl outline-none text-sm transition-all focus:ring-2 font-mono ${tokens.input}`} />
        </div>
      </div>

      <div className={`pt-5 border-t flex items-center justify-between ${tokens.divider}`}>
        <p className={`text-xs ${tokens.muted}`}>
          {projectId ? (allFilled ? '✓ All fields filled' : 'Fill all fields to save') : 'No project selected'}
        </p>
        <div className="flex items-center space-x-3">
          <button type="button" onClick={handleTest} disabled={testing || saving || !allFilled} className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all disabled:opacity-40 flex items-center space-x-2 ${isDark ? 'bg-[#1e293b] border border-[#334155] text-gray-300 hover:border-[#c8f542]/40 hover:text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            {testing ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiMail} className="w-4 h-4" />}
            <span>{testing ? 'Sending...' : 'Send Test'}</span>
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !projectId} className="text-black px-6 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm disabled:opacity-40 flex items-center space-x-2 hover:brightness-110" style={{ backgroundColor: ACCENT }}>
            {saving ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
            <span>{saving ? 'Saving...' : 'Save Credentials'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function StoreSettingsPanel({ projectId, isDark = true }) {
  const [activeTab, setActiveTab] = useState('Store Details');
  const tokens = useTokens(isDark);

  const renderTab = () => {
    switch (activeTab) {
      case 'Store Details':        return <StoreDetailsTab projectId={projectId} isDark={isDark} />;
      case 'General':              return <GeneralTab projectId={projectId} isDark={isDark} />;
      case 'Shipping':             return <ShippingTab projectId={projectId} isDark={isDark} />;
      case 'Payments':             return <PaymentsTab projectId={projectId} isDark={isDark} />;
      case 'EmailJS Integration':  return <EmailJSIntegrationTab projectId={projectId} isDark={isDark} />;
      default:                     return <ComingSoonTab isDark={isDark} />;
    }
  };

  return (
    <div className={`absolute inset-0 flex flex-col ${tokens.pageBg}`}>
      <div className={`shrink-0 px-8 pt-8 pb-0 border-b ${tokens.pageBg} ${tokens.border}`}>
        <h1 className={`text-3xl font-bold mb-6 ${tokens.title}`}>Settings</h1>
        <div className="flex items-center space-x-1 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab} type="button" onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-all border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab
                  ? isDark ? 'border-[#c8f542] text-[#c8f542] bg-[#1e293b]' : 'border-[#c8f542] text-[#c8f542] bg-white'
                  : isDark ? 'border-transparent text-gray-400 hover:text-[#c8f542]' : 'border-transparent text-gray-500 hover:text-[#c8f542]'
              }`}>
              {tab}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="max-w-4xl mx-auto px-8 py-8 pb-16">
          {renderTab()}
        </div>
      </div>
    </div>
  );
}